import React, { useEffect, useState, useCallback } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import scoreAPI from '../services/scoreApi';
import { publicAssetUrl } from '../utils/publicAssetUrl';

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.55);
  z-index: 10050;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
`;

const Panel = styled.div`
  background: #fff;
  border-radius: 16px;
  max-width: 520px;
  width: 100%;
  max-height: 90vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: 0 24px 48px rgba(0, 0, 0, 0.18);
`;

const Head = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 18px 20px 14px;
  border-bottom: 1px solid #e5e7eb;
  background: linear-gradient(135deg, #f8fafc 0%, #eff6ff 100%);
`;

const HeadMain = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  min-width: 0;
`;

const Logo = styled.img`
  width: 52px;
  height: 52px;
  border-radius: 12px;
  object-fit: cover;
  border: 2px solid #e2e8f0;
  flex-shrink: 0;
`;

const LogoPh = styled.div`
  width: 52px;
  height: 52px;
  border-radius: 12px;
  background: #e2e8f0;
  color: #475569;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 800;
  font-size: 1.1rem;
  flex-shrink: 0;
`;

const TitleBlock = styled.div`
  min-width: 0;
`;

const Title = styled.h2`
  margin: 0;
  font-size: 1.15rem;
  font-weight: 800;
  color: #0f172a;
  line-height: 1.25;
`;

const Sub = styled.div`
  font-size: 0.8rem;
  color: #64748b;
  margin-top: 4px;
`;

const CloseBtn = styled.button`
  border: none;
  background: rgba(255, 255, 255, 0.8);
  width: 36px;
  height: 36px;
  border-radius: 10px;
  font-size: 1.35rem;
  line-height: 1;
  cursor: pointer;
  color: #64748b;
  flex-shrink: 0;
  &:hover {
    background: #f1f5f9;
    color: #0f172a;
  }
`;

const Body = styled.div`
  padding: 14px 18px 18px;
  overflow-y: auto;
`;

const StatGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
  margin-bottom: 16px;
`;

const StatCell = styled.div`
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 8px 10px;
  text-align: center;
`;

const StatVal = styled.div`
  font-size: 1rem;
  font-weight: 800;
  color: #043663;
`;

const StatLbl = styled.div`
  font-size: 0.65rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #64748b;
  margin-top: 2px;
`;

const SectionTitle = styled.h3`
  margin: 0 0 8px;
  font-size: 0.72rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #64748b;
`;

const PlayerCard = styled.div`
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 10px 12px;
  margin-bottom: 10px;
  display: flex;
  gap: 12px;
`;

const PAvatar = styled.img`
  width: 44px;
  height: 44px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
  border: 2px solid #e5e7eb;
`;

const PAvatarPh = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  color: #475569;
  flex-shrink: 0;
`;

const PMeta = styled.div`
  flex: 1;
  min-width: 0;
  font-size: 0.8rem;
  line-height: 1.45;
  color: #334155;
`;

const PName = styled.div`
  font-weight: 700;
  color: #0f172a;
  margin-bottom: 4px;
`;

const DL = styled.dl`
  margin: 0;
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 2px 10px;
  font-size: 0.75rem;
`;

const DT = styled.dt`
  color: #94a3b8;
  margin: 0;
  font-weight: 600;
`;

const DD = styled.dd`
  margin: 0;
  color: #1e293b;
`;

const ResultRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 10px;
  font-size: 0.78rem;
  padding: 6px 0;
  border-bottom: 1px solid #f1f5f9;
  &:last-child {
    border-bottom: none;
  }
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 16px;
  padding-top: 14px;
  border-top: 1px solid #e5e7eb;
`;

const Btn = styled.button`
  border: none;
  border-radius: 10px;
  padding: 10px 16px;
  font-size: 0.85rem;
  font-weight: 700;
  cursor: pointer;
  background: linear-gradient(135deg, #043663 0%, #0e7490 100%);
  color: #fff;
  &:hover {
    filter: brightness(1.05);
  }
`;

const BtnGhost = styled.button`
  border: 1px solid #cbd5e1;
  border-radius: 10px;
  padding: 10px 16px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  background: #fff;
  color: #475569;
  &:hover {
    background: #f8fafc;
  }
`;

const Muted = styled.p`
  margin: 0;
  color: #94a3b8;
  font-size: 0.85rem;
`;

function fmt(v) {
  if (v == null || v === '') return '—';
  return String(v);
}

/**
 * @param {object} props
 * @param {boolean} props.isOpen
 * @param {() => void} props.onClose
 * @param {string} props.matchId — league match id
 * @param {string|null} props.teamId — viewer team id (player: required query param)
 * @param {{ id?: string, opponent?: string, opponentId?: string, dateDisplay?: string } | null} props.matchForChat — optional; same shape as dashboard match for opening chat
 * @param {() => void} [props.onOpenChat] — if provided, opens in-app chat with matchForChat
 * @param {() => void} [props.onNavigateToChat] — if provided (e.g. player shell), navigate to workspace with chat
 */
const OpponentTeamSheetModal = ({
  isOpen,
  onClose,
  matchId,
  teamId,
  matchForChat,
  onOpenChat,
  onNavigateToChat,
}) => {
  const { t, i18n } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    if (!matchId || !teamId) return;
    setLoading(true);
    setErr(null);
    try {
      const res = await scoreAPI.getOpponentTeamSheet(matchId, teamId);
      if (res?.success && res.data) setData(res.data);
      else setErr(res?.error || 'load failed');
    } catch (e) {
      setErr(e?.response?.data?.error || e?.message || 'load failed');
    } finally {
      setLoading(false);
    }
  }, [matchId, teamId]);

  useEffect(() => {
    if (isOpen && matchId && teamId) load();
  }, [isOpen, matchId, teamId, load]);

  if (!isOpen) return null;

  const team = data?.team;
  const logoSrc = team?.logoUrl ? publicAssetUrl(team.logoUrl) : '';
  const st = data?.standing;

  const langLabel = (code) => {
    if (!code) return '—';
    try {
      const dn = new Intl.DisplayNames([i18n.language || 'en'], { type: 'language' });
      return dn.of(String(code).replace('_', '-').slice(0, 5)) || code;
    } catch {
      return code;
    }
  };

  return (
    <Overlay role="presentation" onClick={onClose}>
      <Panel role="dialog" aria-modal="true" aria-labelledby="opp-sheet-title" onClick={(e) => e.stopPropagation()}>
        <Head>
          <HeadMain>
            {logoSrc ? <Logo src={logoSrc} alt="" /> : <LogoPh>{(team?.name || '?').trim().slice(0, 1).toUpperCase()}</LogoPh>}
            <TitleBlock>
              <Title id="opp-sheet-title">{team?.name || t('teamDashboardPage.opponentSheetFallbackTeam')}</Title>
              {team?.skillLevel ? <Sub>{t('teamDashboardPage.opponentSheetTeamLevel', { level: team.skillLevel })}</Sub> : null}
            </TitleBlock>
          </HeadMain>
          <CloseBtn type="button" onClick={onClose} aria-label={t('teamDashboardPage.opponentSheetClose')}>
            ×
          </CloseBtn>
        </Head>
        <Body>
          {loading ? <Muted>{t('teamDashboardPage.opponentSheetLoading')}</Muted> : null}
          {err && !loading ? <Muted style={{ color: '#b91c1c' }}>{err}</Muted> : null}

          {!loading && !err && st ? (
            <>
              <SectionTitle>{t('teamDashboardPage.opponentSheetStats')}</SectionTitle>
              <StatGrid>
                <StatCell>
                  <StatVal>{fmt(st.wins)}</StatVal>
                  <StatLbl>{t('teamDashboardPage.opponentSheetStatWins')}</StatLbl>
                </StatCell>
                <StatCell>
                  <StatVal>{fmt(st.losses)}</StatVal>
                  <StatLbl>{t('teamDashboardPage.opponentSheetStatLosses')}</StatLbl>
                </StatCell>
                <StatCell>
                  <StatVal>{fmt(st.points)}</StatVal>
                  <StatLbl>{t('teamDashboardPage.opponentSheetStatPoints')}</StatLbl>
                </StatCell>
              </StatGrid>
            </>
          ) : null}

          {!loading && !err && Array.isArray(data?.players) && data.players.length > 0 ? (
            <>
              <SectionTitle>{t('teamDashboardPage.opponentSheetPlayers')}</SectionTitle>
              {data.players.map((p) => {
                const name = [p.first_name, p.last_name].filter(Boolean).join(' ').trim() || '—';
                const pic =
                  p.profile_picture_url != null && String(p.profile_picture_url).trim()
                    ? publicAssetUrl(p.profile_picture_url)
                    : '';
                const initials = name
                  .split(/\s+/)
                  .map((x) => x[0])
                  .join('')
                  .slice(0, 2)
                  .toUpperCase();
                return (
                  <PlayerCard key={p.member_id || name}>
                    {pic ? <PAvatar src={pic} alt="" /> : <PAvatarPh>{initials || '?'}</PAvatarPh>}
                    <PMeta>
                      <PName>{name}</PName>
                      <DL>
                        <DT>{t('teamDashboardPage.opponentSheetLanguage')}</DT>
                        <DD>{langLabel(p.ui_locale)}</DD>
                        <DT>{t('teamDashboardPage.opponentSheetCanton')}</DT>
                        <DD>{fmt(p.home_canton)}</DD>
                        <DT>{t('teamDashboardPage.opponentSheetHomeClub')}</DT>
                        <DD>{fmt(p.home_club)}</DD>
                        <DT>{t('teamDashboardPage.opponentSheetLevel')}</DT>
                        <DD>{fmt(p.skill_level)}</DD>
                        <DT>{t('teamDashboardPage.opponentSheetHand')}</DT>
                        <DD>{fmt(p.dominant_hand)}</DD>
                        <DT>{t('teamDashboardPage.opponentSheetSide')}</DT>
                        <DD>{fmt(p.preferred_court_side)}</DD>
                      </DL>
                    </PMeta>
                  </PlayerCard>
                );
              })}
            </>
          ) : null}

          {!loading && !err && Array.isArray(data?.recentResults) && data.recentResults.length > 0 ? (
            <>
              <SectionTitle style={{ marginTop: 8 }}>{t('teamDashboardPage.opponentSheetRecent')}</SectionTitle>
              {data.recentResults.map((r) => (
                <ResultRow key={r.matchId}>
                  <span>
                    {t('teamDashboardPage.vsOpponent', { name: r.opponentName })}
                    {r.matchDate ? <span style={{ color: '#94a3b8' }}> · {r.matchDate}</span> : null}
                  </span>
                  <strong style={{ color: '#0f172a' }}>{r.scoreLine}</strong>
                </ResultRow>
              ))}
            </>
          ) : null}

          <Actions>
            {typeof onNavigateToChat === 'function' ? (
              <Btn
                type="button"
                onClick={() => {
                  onClose();
                  onNavigateToChat();
                }}
              >
                {t('teamDashboardPage.opponentSheetMessage')}
              </Btn>
            ) : typeof onOpenChat === 'function' && matchForChat?.id ? (
              <Btn
                type="button"
                onClick={() => {
                  onClose();
                  onOpenChat(matchForChat);
                }}
              >
                {t('teamDashboardPage.opponentSheetMessage')}
              </Btn>
            ) : null}
            <BtnGhost type="button" onClick={onClose}>
              {t('teamDashboardPage.opponentSheetClose')}
            </BtnGhost>
          </Actions>
        </Body>
      </Panel>
    </Overlay>
  );
};

export default OpponentTeamSheetModal;
